/**
 * Where to send someone to book an appointment for a patient.
 *
 * Replaces the Yoman invite, which was an external calendar's booking form
 * reached by a round trip to generate a URL. The in-app page offers this
 * clinic's own availability, so the link is just the patient's scheduling
 * page and needs no call to build.
 */
import { meetingService } from './meetingService';

export const PATIENT_APP_ORIGIN =
  process.env.REACT_APP_PATIENT_APP_URL || window.location.origin;

/** What a clinician has already decided, if anything. */
export interface SchedulingChoice {
  /** Book THIS kind of meeting, rather than whatever the patient is due. */
  meetingTypeId?: number | null;
  /** With THEM. Omitted means the pool, which is a choice in its own right. */
  therapistId?: number | null;
  /** Open the BOOKING screen even if the patient already has an
   *  appointment - a clinician arranging a second future meeting. Without
   *  it the page opens on the existing one and only offers to move it. */
  book?: boolean;
  /** Which meeting to manage. Only matters when the patient has more than
   *  one active; otherwise the page shows what it always did. */
  meetingId?: number | null;
}

/** The patient's scheduling page, absolute so it can be opened in a tab.
 *
 *  A choice is carried in the query string so the page can honour it. Without
 *  one the page works out what is due from the patient's own state, which is
 *  right for a patient following their own link and wrong for a clinician who
 *  has already chosen.
 */
export const patientScheduleUrl = (
  patientUuid: string,
  choice?: SchedulingChoice,
): string => {
  const params = new URLSearchParams();
  if (choice?.meetingTypeId) params.set('meeting_type_id', String(choice.meetingTypeId));
  if (choice?.therapistId) params.set('therapist_id', String(choice.therapistId));
  if (choice?.book) params.set('book', 'true');
  if (choice?.meetingId) params.set('meeting_id', String(choice.meetingId));
  const qs = params.toString();
  return `${PATIENT_APP_ORIGIN.replace(/\/$/, '')}/schedule/${patientUuid}${qs ? `?${qs}` : ''}`;
};

/** Open it, the way the invite used to be opened.
 *
 *  `patientId` makes this a STAFF opening: the link is minted by the server,
 *  which signs a marker naming the signed-in user, so the booking is recorded
 *  as arranged by them. Built here it carried no marker at all, and every
 *  meeting a clinician arranged this way was filed as the patient's own.
 *
 *  The marker cannot be assembled in the browser - it is signed with a server
 *  secret, and a client-supplied issuer could name anyone.
 *
 *  The tab is opened BEFORE the request and pointed afterwards: a window.open
 *  that happens after an await has lost the click that justified it, and
 *  browsers block it.
 *
 *  Returns false when the patient has no uuid: there is no scheduling page
 *  for them, and opening /schedule/null would show a "not found" that reads
 *  like the feature is broken rather than like this patient is incomplete.
 */
export const openPatientScheduling = (
  patientUuid: string | null | undefined,
  choice?: SchedulingChoice,
  patientId?: number | null,
): boolean => {
  if (!patientUuid) return false;
  if (!patientId) {
    // The patient's own link - nobody to attribute it to.
    window.open(patientScheduleUrl(patientUuid, choice), '_blank', 'noopener,noreferrer');
    return true;
  }
  const tab = window.open('', '_blank');
  if (tab) {
    try {
      tab.opener = null;
    } catch {
      /* some browsers refuse the assignment; the link is still safe to open */
    }
  }
  meetingService
    .getScheduleLink(patientId)
    .then(({ link }) => {
      // The server's link carries the signed marker; the choice rides on top.
      const url = new URL(link);
      if (choice?.meetingTypeId) url.searchParams.set('meeting_type_id', String(choice.meetingTypeId));
      if (choice?.therapistId) url.searchParams.set('therapist_id', String(choice.therapistId));
      if (choice?.book) url.searchParams.set('book', 'true');
      if (choice?.meetingId) url.searchParams.set('meeting_id', String(choice.meetingId));
      if (tab) tab.location.href = url.toString();
      else window.open(url.toString(), '_blank', 'noopener,noreferrer');
    })
    .catch(() => {
      // Still open the page: an unattributed booking beats no booking. The
      // marker is the only thing lost.
      const fallback = patientScheduleUrl(patientUuid, choice);
      if (tab) tab.location.href = fallback;
      else window.open(fallback, '_blank', 'noopener,noreferrer');
    });
  return true;
};
